import React, { useState, useRef, useCallback, useEffect } from 'react';
import './SplitScreenLayout.css';

const SplitScreenLayout = ({ leftComponent, rightComponent }) => {
  const [splitPercentage, setSplitPercentage] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const dividerWidth = 8; // Width of the divider in pixels
    const availableWidth = containerRect.width - dividerWidth;
    const offsetX = e.clientX - containerRect.left;
    const percentage = (offsetX / availableWidth) * 100;

    // Clamp between 20% and 80% for better range
    const clampedPercentage = Math.min(Math.max(percentage, 20), 80);
    setSplitPercentage(clampedPercentage);
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // Prevent text selection during drag
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Calculate actual pixel widths based on container
  const containerWidth = containerRef.current?.getBoundingClientRect().width || 1000;
  const dividerWidth = 8;
  const leftWidth = (containerWidth * splitPercentage / 100) - (dividerWidth / 2);
  const rightWidth = (containerWidth * (100 - splitPercentage) / 100) - (dividerWidth / 2);

  return (
    <div className={`split-screen-layout ${isDragging ? 'dragging' : ''}`} ref={containerRef}>
      <div 
        className="split-screen-panel split-screen-left"
        style={{ 
          width: `${leftWidth}px`,
          minWidth: `${leftWidth}px`,
          maxWidth: `${leftWidth}px`,
          flex: '0 0 auto'
        }}
      >
        {leftComponent}
      </div>

      <div 
        className="split-screen-divider"
        onMouseDown={handleMouseDown}
        role="separator"
        aria-valuenow={splitPercentage}
        aria-valuemin={20}
        aria-valuemax={80}
        aria-label="Draggable divider"
        style={{ flex: `0 0 ${dividerWidth}px` }}
      >
        <div className="split-screen-divider-handle">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="8" r="1.5" fill="currentColor"/>
            <circle cx="10" cy="12" r="1.5" fill="currentColor"/>
            <circle cx="10" cy="16" r="1.5" fill="currentColor"/>
            <circle cx="14" cy="8" r="1.5" fill="currentColor"/>
            <circle cx="14" cy="12" r="1.5" fill="currentColor"/>
            <circle cx="14" cy="16" r="1.5" fill="currentColor"/>
          </svg>
        </div>
      </div>

      <div 
        className="split-screen-panel split-screen-right"
        style={{ 
          width: `${rightWidth}px`,
          minWidth: `${rightWidth}px`,
          maxWidth: `${rightWidth}px`,
          flex: '0 0 auto'
        }}
      >
        {rightComponent}
      </div>
    </div>
  );
};

export default SplitScreenLayout;

